"use client";

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import styles from "../css/splitPayment.module.css";
import { LuBanknote, LuFileText, LuBuilding2 } from "react-icons/lu";
import { FiCreditCard, FiTruck, FiSmartphone } from "react-icons/fi";

export default function SplitPaymentStep2View() {
  const location = useLocation();
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);

  const method1 = location.state && typeof location.state.method1 === "string"
    ? location.state.method1
    : null;
  const fromDebtors = !!(location.state && location.state.fromDebtors);

  useEffect(() => {
    if (location.state && typeof location.state.total === "number") {
      setTotal(location.state.total);
      return;
    }

    try {
      const raw = localStorage.getItem("frontshop_basket");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const subtotal = parsed.reduce(
            (sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 0),
            0
          );
          const tax = subtotal * 0.15;
          setTotal(subtotal + tax);
        }
      }
    } catch {
      // ignore storage parse errors
    }
  }, [location.state]);

  const displayAmount = total.toFixed(2);

  const handleBack = () => {
    if (method1 === "cash") {
      navigate("/frontshop/payment-types/split/method1=cash", {
        state: { total, splitFlow: true, method1: "cash", fromDebtors },
      });
      return;
    }

    if (method1 === "card") {
      navigate("/frontshop/payment-types/split/method1=card", {
        state: {
          total,
          splitFlow: true,
          method1: "card",
          fromDebtors,
          paymentMethod: "Card",
        },
      });
      return;
    }

    if (method1 === "nedbank") {
      navigate("/frontshop/payment-types/split/method1=nedbank", {
        state: {
          total,
          splitFlow: true,
          method1: "nedbank",
          fromDebtors,
          paymentMethod: "Nedbank",
        },
      });
      return;
    }

    if (method1 === "ikhokha") {
      navigate("/frontshop/payment-types/split/method1=ikhokha", {
        state: {
          total,
          splitFlow: true,
          method1: "ikhokha",
          fromDebtors,
          paymentMethod: "Ikhokha",
        },
      });
      return;
    }

    if (method1 === "medicalAid") {
      navigate("/frontshop/payment-types/split/medical-aid/payment", {
        state: { total, fromDebtors },
      });
      return;
    }

    navigate("/frontshop/payment-types/split", {
      state: { total, fromDebtors },
    });
  };

  const baseMethods = [
    { id: "cash", label: "Cash", icon: LuBanknote, className: styles.cash },
    { id: "card", label: "Card", icon: FiCreditCard, className: styles.card },
    { id: "cod", label: "COD", icon: FiTruck, className: styles.cod },
    { id: "credit", label: "Credit", icon: LuFileText, className: styles.credit },
    { id: "nedbank", label: "Nedbank", icon: LuBuilding2, className: styles.nedbank },
    { id: "ikhokha", label: "Ikhokha", icon: FiSmartphone, className: styles.ikhokha },
  ];

  const methods = (() => {
    let filtered = fromDebtors
      ? baseMethods.filter((m) =>
          ["cash", "card", "nedbank", "ikhokha"].includes(m.id)
        )
      : baseMethods;

    // Always hide COD and Credit from the second-step options
    filtered = filtered.filter((m) => m.id !== "cod" && m.id !== "credit");

    // If method1 is cash, do not allow cash again for method2
    if (method1 === "cash") {
      filtered = filtered.filter((m) => m.id !== "cash");
    }

    if (!method1) return filtered;

    // Only hide the first selected method for these types in step 2
    const hideInStep2 = ["cash", "nedbank", "ikhokha"];
    if (!hideInStep2.includes(method1)) return filtered;

    return filtered.filter((m) => m.id !== method1);
  })();

  const methodsLayoutClass = methods.length === 4 ? styles.methodsGridFour : "";

  const handleContinue = () => {
    if (!selected) return;

    if (selected === "cash") {
      navigate("/frontshop/payment-types/split/step2/method2=cash", {
        state: { total },
      });
      return;
    }
    if (selected === "cod") {
      navigate("/frontshop/payment-types/split/step2/method2=cod", {
        state: { total },
      });
      return;
    }
    if (selected === "credit") {
      navigate("/frontshop/payment-types/split/step2/method2=credit", {
        state: { total },
      });
      return;
    }
    if (selected === "card") {
      navigate("/frontshop/payment-types/split/step2/method2=card", {
        state: { total, paymentMethod: "Card" },
      });
      return;
    }
    if (selected === "nedbank") {
      navigate("/frontshop/payment-types/split/step2/method2=nedbank", {
        state: { total, paymentMethod: "Nedbank" },
      });
      return;
    }
    if (selected === "ikhokha") {
      navigate("/frontshop/payment-types/split/step2/method2=ikhokha", {
        state: { total, paymentMethod: "Ikhokha" },
      });
      return;
    }
  };

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />

      <main className={styles.main}>
        <div className={styles.contentCard}>
          <header className={styles.header}>
            <h1 className={styles.title}>Split Payment</h1>
            <p className={styles.subtitle}>
              Total Amount: <span className={styles.amount}>R {displayAmount}</span>
            </p>
          </header>

          <p className={styles.stepLabel}>Select method 2 to split between:</p>

          <section className={`${styles.methodsGrid} ${methodsLayoutClass}`}>
            {methods.map((method) => {
              const Icon = method.icon;
              const isSelected = selected === method.id;
              return (
                <button
                  key={method.id}
                  type="button"
                  className={`${styles.methodCard} ${method.className} ${
                    isSelected ? styles.methodCardSelected : ""
                  }`}
                  onClick={() => setSelected(method.id)}
                >
                  <div className={styles.iconWrap}>
                    <Icon />
                  </div>
                  <div className={styles.methodLabel}>{method.label}</div>
                </button>
              );
            })}
          </section>

          <div className={styles.continueRow}>
            <Button
              type="button"
              variant="approve"
              className={styles.continueButton}
              onClick={handleContinue}
            >
              Continue to payment
            </Button>
          </div>
        </div>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
